/**
 * Client-side trusted device identity for attendance clock-in.
 * Sends deviceId on every time-in (root + trustedDevice object).
 * Skipped for areCool users — same exemption as mobile blocking.
 */

const STORAGE_KEY = "hrm_trusted_device_id";

export interface TrustedDevicePayload {
  /** Stable random id persisted in this browser profile */
  deviceId: string;
  userAgent: string;
  platform: string;
  /** IANA timezone, e.g. Asia/Karachi */
  timezone: string;
  /** e.g. en-US */
  language: string;
  /** Screen dimensions (not used for mobile detection) */
  screen: string;
}

function createDeviceId(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `dev-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

/** Returns the persisted device id, creating one on first use. */
export function getOrCreateDeviceId(): string {
  if (typeof window === "undefined") {
    return createDeviceId();
  }
  try {
    const existing = localStorage.getItem(STORAGE_KEY);
    if (existing && existing.trim().length > 0) {
      return existing.trim();
    }
    const id = createDeviceId();
    localStorage.setItem(STORAGE_KEY, id);
    return id;
  } catch {
    return createDeviceId();
  }
}

/** Metadata sent with time-in for future trusted-device enforcement. */
export function buildTrustedDevicePayload(): TrustedDevicePayload {
  const screen =
    typeof window !== "undefined"
      ? `${window.screen.width}x${window.screen.height}`
      : "unknown";

  return {
    deviceId: getOrCreateDeviceId(),
    userAgent: navigator.userAgent || "",
    platform: navigator.platform || "",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "",
    language: navigator.language || "",
    screen,
  };
}

export interface TimeInClientContext {
  deviceId?: string;
  isMobile: boolean;
  isDesktopMode: boolean;
  forceDesktop: boolean;
  trustedDevice?: Pick<TrustedDevicePayload, "deviceId"> &
    Partial<Omit<TrustedDevicePayload, "deviceId">>;
}

/** Alias — same client context is used for time-in and time-out. */
export type AttendanceClientContext = TimeInClientContext;

/**
 * Builds the client portion of a time-in / time-out request body.
 * Trusted device fields are omitted for areCool users.
 */
export function buildTimeInClientContext(
  mobileEnvironment: {
    isMobile: boolean;
    isDesktopSiteOnMobile: boolean;
  },
  user: { areCool?: boolean | null } | null | undefined,
): TimeInClientContext {
  const isDesktopMode = mobileEnvironment.isDesktopSiteOnMobile;
  const base: TimeInClientContext = {
    isMobile: mobileEnvironment.isMobile,
    isDesktopMode,
    forceDesktop: isDesktopMode,
  };

  if (user?.areCool === true) {
    return base;
  }

  const payload = buildTrustedDevicePayload();

  return {
    ...base,
    deviceId: payload.deviceId,
    trustedDevice: payload,
  };
}

/** @deprecated Use buildTimeInClientContext — kept as alias for clarity at call sites. */
export const buildAttendanceClientContext = buildTimeInClientContext;

export function buildTimeOutRequestBody(
  userId: string,
  mobileEnvironment: {
    isMobile: boolean;
    isDesktopSiteOnMobile: boolean;
  },
  user: { areCool?: boolean | null } | null | undefined,
  timeOut: Date | string = new Date(),
): Record<string, unknown> {
  const timeOutIso =
    timeOut instanceof Date ? timeOut.toISOString() : timeOut;

  return {
    userId,
    timeOut: timeOutIso,
    ...buildTimeInClientContext(mobileEnvironment, user),
  };
}
